import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Home as HomeIcon, ScrollText, Mail, Phone, MapPin } from 'lucide-react';

const SECTIONS = [
  {
    h: '1. Acceptance of these Terms',
    p: [
      'Welcome to TMB Global News ("TMB Global News", "we", "us", or "our"). By accessing or using this website, you agree to be bound by these Terms & Conditions and by our practices described below. If you do not agree with any part of these Terms, please discontinue use of the website.',
    ],
  },
  {
    h: '2. Nature of our service — a news aggregator',
    p: [
      'TMB Global News is a news aggregation service. We automatically collect and organize publicly available headlines, short summaries, publication dates, source names, thumbnail images and links from third-party news publishers and public RSS/news feeds, with a focus on news relevant to the State of Michigan, USA.',
      'We do not employ the reporters who write the underlying stories and we do not claim ownership of the original articles. Each item on this website links back to the original publisher, where the full article can be read. Our role is simply to bring the latest Michigan-related news together in one convenient place.',
    ],
  },
  {
    h: '3. No endorsement — content is not our opinion',
    p: [
      'All news headlines, summaries and linked articles displayed on TMB Global News originate from independent third-party sources. Such content does NOT represent the views, opinions, positions or endorsements of TMB Global News, its owners, staff or partners.',
      'Any opinions, statements, or claims expressed in aggregated content are those of the respective authors and publishers alone. TMB Global News acts solely as a neutral conduit that surfaces publicly available news and directs readers to the original source.',
    ],
  },
  {
    h: '4. Intellectual property, attribution & fair use',
    p: [
      'All trademarks, logos, article text, and images referenced remain the property of their respective owners and original publishers. TMB Global News displays only limited material (such as headlines, brief summaries, and small thumbnail images) together with clear attribution and a link to the source, consistent with common industry practice and fair-use principles for news aggregation and commentary.',
      'We respect the rights of content owners. If you are a rights holder and believe your content should not appear on TMB Global News, please contact us using the details below and we will promptly review and, where appropriate, remove the material.',
    ],
  },
  {
    h: '5. Accuracy & availability',
    p: [
      'Aggregated content is provided on an "as is" and "as available" basis. Because news is gathered automatically from external sources, TMB Global News does not warrant the accuracy, completeness, timeliness or reliability of any headline, summary or linked article. News is refreshed periodically (typically twice daily) and may not reflect the most current information at all times.',
    ],
  },
  {
    h: '6. Third-party links',
    p: [
      'This website contains links to third-party websites operated by news publishers and other parties. We do not control and are not responsible for the content, privacy practices, or availability of those external sites. Accessing third-party links is at your own risk and subject to the terms of those sites.',
    ],
  },
  {
    h: '7. Advertising',
    p: [
      'TMB Global News displays advertising placements. Advertisements and sponsored placements do not constitute an endorsement by TMB Global News of any product, service or advertiser. Inquiries regarding advertising can be directed to the contact details below.',
    ],
  },
  {
    h: '8. Limitation of liability',
    p: [
      'To the fullest extent permitted by applicable law, TMB Global News and its affiliates shall not be liable for any indirect, incidental, special, consequential or punitive damages, or any loss arising from your use of, or reliance on, any content aggregated or linked on this website.',
    ],
  },
  {
    h: '9. Changes to these Terms',
    p: [
      'We may update these Terms & Conditions from time to time. Any changes become effective when posted on this page. Your continued use of the website after changes are posted constitutes your acceptance of the revised Terms.',
    ],
  },
  {
    h: '10. Governing law',
    p: [
      'These Terms are governed by and construed in accordance with the laws of the State of Michigan, United States of America, without regard to its conflict-of-law principles.',
    ],
  },
];

export default function Terms() {
  return (
    <div>
      <div className="text-white" style={{ background: 'linear-gradient(120deg, #0b3d26 0%, #0f5132 55%, #157347 100%)' }}>
        <div className="max-w-[900px] mx-auto px-4 py-12">
          <nav className="flex items-center gap-1.5 text-[13px] text-[#a9d4bd] mb-5">
            <Link to="/" className="inline-flex items-center gap-1 hover:text-white"><HomeIcon className="w-3.5 h-3.5" />Home</Link>
            <ChevronRight className="w-3.5 h-3.5" /><span className="text-white font-semibold">Terms &amp; Conditions</span>
          </nav>
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-3 py-1 text-[13px] font-medium text-[#cdeddb] mb-4">
            <ScrollText className="w-4 h-4" /> Legal
          </div>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl">Terms &amp; Conditions</h1>
          <p className="text-[#cdeddb] mt-3 max-w-2xl">How TMB Global News aggregates Michigan news, and the terms that govern your use of this website.</p>
        </div>
      </div>

      <div className="max-w-[900px] mx-auto px-4 py-10">
        <div className="rounded-xl border border-[#e3ece7] bg-[#eef8f2] p-5 mb-8">
          <p className="text-[#26332d] leading-relaxed">
            <span className="font-semibold text-[#0f5132]">In short:</span> TMB Global News is an aggregator that brings you the
            latest Michigan-related news from across the web. The stories we link to are produced by independent publishers and
            do not reflect the opinions of TMB Global News. We display headlines, short summaries and thumbnails with attribution
            and always link back to the original source.
          </p>
        </div>

        <div className="space-y-8">
          {SECTIONS.map((s) => (
            <section key={s.h}>
              <h2 className="font-display font-bold text-xl text-[#0b3d26] mb-2">{s.h}</h2>
              {s.p.map((para, i) => (
                <p key={i} className="text-[16px] leading-[1.8] text-[#26332d] mb-3">{para}</p>
              ))}
            </section>
          ))}

          <section>
            <h2 className="font-display font-bold text-xl text-[#0b3d26] mb-3">11. Contact us</h2>
            <p className="text-[16px] leading-[1.8] text-[#26332d] mb-4">
              Questions about these Terms, content removal requests, or advertising inquiries can be sent to:
            </p>
            <ul className="space-y-3">
              <li className="flex items-center gap-3 text-[#14241d]"><span className="w-9 h-9 rounded-full bg-[#157347] text-white flex items-center justify-center"><Mail className="w-4 h-4" /></span><a href="mailto:info@tmbglobalnews.com" className="text-[#157347] font-medium">info@tmbglobalnews.com</a></li>
              <li className="flex items-center gap-3 text-[#14241d]"><span className="w-9 h-9 rounded-full bg-[#157347] text-white flex items-center justify-center"><Phone className="w-4 h-4" /></span><a href="tel:+19899922600" className="font-medium">+1 989.992.2600</a></li>
              <li className="flex items-center gap-3 text-[#14241d]"><span className="w-9 h-9 rounded-full bg-[#157347] text-white flex items-center justify-center"><MapPin className="w-4 h-4" /></span>301 E Genesee Ave, Suite 201-B, Saginaw, MI 48607, USA</li>
            </ul>
          </section>

          <p className="text-sm text-[#9aa8a1] pt-4 border-t border-[#e3ece7]">Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
        </div>
      </div>
    </div>
  );
}
